import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import BarChartRenderer from './BarChartRenderer';
import {
  hasSeries, buildSeriesLayout,
  renderXAxisLabel, renderYAxisLabel, yLabelNeedsWrap,
  truncateLabel, xTickHeight, xTicksNeedRotation, tickLabelLines,
  formatChartNumber, xLabelDisplay,
} from './chartUtils';

// Stacked and grouped bars share everything but how the segments are laid out:
// stacked sums the series vertically, grouped places them side by side. Both key
// their colour off the series (PRD-1370 #13/#16) so multi-series charts render
// distinctly and get a legend — unlike the flat BarChartRenderer, whose scaleBand
// deduped duplicate labels and collapsed the chart.
function StackedBarRenderer({ data, width, height, xLabel, yLabel, mode = 'stacked' }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0 || !width || !height) return;
    // No second dimension → nothing to stack/group; the plain renderer handles it.
    if (!hasSeries(data)) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { labels, seriesKeys, rows } = buildSeriesLayout(data);
    const color = d3.scaleOrdinal().domain(seriesKeys).range(d3.schemeTableau10);

    const fontSize = 11;
    const charWidth = fontSize * 0.55;
    const baseLeft = 70;

    // Legend sits above the plot; height grows with the number of series rows.
    const legendItemGap = 16;
    const swatch = 12;
    const estLegendPerItem = (k) => swatch + 6 + String(k).length * charWidth + legendItemGap;
    const legendRowH = 18;
    let legendRows = 1;
    { // pre-measure how many rows the legend wraps to at this width
      let x = 0;
      legendRows = 1;
      seriesKeys.forEach((k) => {
        const w = estLegendPerItem(k);
        if (x + w > width && x > 0) { legendRows += 1; x = 0; }
        x += w;
      });
    }
    const legendH = legendRows * legendRowH + 6;

    // ISO date buckets read as "Apr 2026"; any other label is shown as sent.
    const showX = xLabelDisplay(labels);
    const shownLabels = labels.map(showX);

    const estBand = (width - baseLeft - 20) / Math.max(labels.length, 1);
    const rotateX = xTicksNeedRotation(shownLabels, estBand);
    const maxCharsPerLine = Math.max(8, Math.floor((estBand * 0.9) / charWidth));
    const xLines = rotateX
      ? shownLabels.map((l) => [truncateLabel(l)])
      : shownLabels.map((l) => tickLabelLines(l, maxCharsPerLine));
    const linesByLabel = new Map(labels.map((l, i) => [l, xLines[i]]));
    const tickH = rotateX
      ? xTickHeight(xLines.map((ls) => ls[0]), fontSize, true)
      : fontSize + 4 + (Math.max(1, ...xLines.map((ls) => ls.length)) - 1) * 14;

    const marginBottom = Math.max(60, tickH + (xLabel ? 24 : 8));
    const wrapsY = yLabelNeedsWrap(yLabel, height - legendH - marginBottom);
    const margin = { top: legendH + 8, right: 20, bottom: marginBottom, left: baseLeft + (wrapsY ? 14 : 0) };
    const w = width - margin.left - margin.right;
    const h = height - margin.top - margin.bottom;
    if (w <= 0 || h <= 0) return;

    svg.attr('width', width).attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    // Legend (top-left, wrapping).
    const legend = svg.append('g').attr('transform', 'translate(0,4)');
    let lx = 0;
    let ly = 0;
    seriesKeys.forEach((key) => {
      const itemW = estLegendPerItem(key);
      if (lx + itemW > width && lx > 0) { lx = 0; ly += legendRowH; }
      const item = legend.append('g').attr('transform', `translate(${lx},${ly})`);
      item.append('rect').attr('width', swatch).attr('height', swatch).attr('rx', 2).attr('fill', color(key));
      item.append('text').attr('x', swatch + 6).attr('y', swatch - 2)
        .attr('font-size', '11px').attr('font-family', 'Roboto, sans-serif').attr('fill', '#212121')
        .text(key);
      lx += itemW;
    });

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const x = d3.scaleBand().domain(labels).range([0, w]).padding(0.3);
    const yMax = mode === 'grouped'
      ? d3.max(rows, (r) => d3.max(seriesKeys, (k) => r[k])) || 0
      : d3.max(rows, (r) => d3.sum(seriesKeys, (k) => r[k])) || 0;
    const y = d3.scaleLinear().domain([0, yMax * 1.05]).range([h, 0]).nice();

    // X axis (shared).
    const xTickText = g.append('g').attr('transform', `translate(0,${h})`)
      .call(d3.axisBottom(x).tickFormat(showX))
      .selectAll('text').attr('font-size', '11px').attr('font-family', 'Roboto, sans-serif');
    if (rotateX) {
      xTickText.text((d) => (linesByLabel.get(String(d)) || [String(d)])[0])
        .attr('transform', 'rotate(-45)').attr('text-anchor', 'end')
        .attr('dx', '-0.3em').attr('dy', '0.3em');
    } else {
      xTickText.each(function (d) {
        const t = d3.select(this);
        const lines = linesByLabel.get(String(d)) || [String(d)];
        t.text(null).attr('dy', null);
        lines.forEach((line, i) => {
          t.append('tspan').attr('x', 0).attr('dy', i === 0 ? '0.71em' : '1.1em').text(line);
        });
      });
    }
    xTickText.append('title').text(showX);

    g.append('g').call(d3.axisLeft(y).ticks(5).tickFormat(formatChartNumber))
      .selectAll('text').attr('font-size', '11px').attr('font-family', 'Roboto, sans-serif');

    if (mode === 'grouped') {
      const xInner = d3.scaleBand().domain(seriesKeys).range([0, x.bandwidth()]).padding(0.08);
      const group = g.selectAll('.group').data(rows).enter().append('g')
        .attr('transform', (r) => `translate(${x(r.label)},0)`);
      group.selectAll('rect').data((r) => seriesKeys.map((k) => ({ key: k, value: r[k] })))
        .enter().append('rect')
        .attr('x', (d) => xInner(d.key))
        .attr('y', (d) => y(d.value))
        .attr('width', xInner.bandwidth())
        .attr('height', (d) => h - y(d.value))
        .attr('fill', (d) => color(d.key))
        .attr('rx', 2)
        .append('title').text((d) => `${d.key}: ${formatChartNumber(d.value)}`);
    } else {
      const stacked = d3.stack().keys(seriesKeys)(rows);
      g.selectAll('.series').data(stacked).enter().append('g')
        .attr('fill', (s) => color(s.key))
        .selectAll('rect').data((s) => s.map((seg) => ({ seg, key: s.key })))
        .enter().append('rect')
        .attr('x', (d) => x(d.seg.data.label))
        .attr('y', (d) => y(d.seg[1]))
        .attr('width', x.bandwidth())
        .attr('height', (d) => Math.max(0, y(d.seg[0]) - y(d.seg[1])))
        .attr('rx', 2)
        .append('title').text((d) => `${d.key}: ${formatChartNumber(d.seg[1] - d.seg[0])}`);
    }

    renderXAxisLabel(g, xLabel, w, h, tickH);
    renderYAxisLabel(g, yLabel, h, margin.left);
  }, [data, width, height, xLabel, yLabel, mode]);

  // Flat data (no series) → reuse the plain bar renderer unchanged.
  if (!hasSeries(data)) {
    return <BarChartRenderer data={data} width={width} height={height} xLabel={xLabel} yLabel={yLabel} />;
  }
  return <svg ref={svgRef} />;
}

export default StackedBarRenderer;
